// Object drawing utilities for floor plan visualization

import { cornerCabinetFace } from '@shared-lib/kitchen-assembly/cornerCabinetFace';
import { angledEndOutline } from '@shared-lib/cabinet-runs/angledEnds';
import { getDormBadge, isDormSmallDecor } from './dormBadges';
import {
  drawElectricalDeviceSymbol,
  drawLightSymbol,
} from './electricalDrawingUtils';
import { lightSymbolOf } from './lightingClassification';
import { isElectricalDeviceType } from './electricalDevices';
import { drawShowerHeadRings } from './plumbingDrawingUtils';
import { kitchenSinkBowls } from '../components/3d/geometry/proceduralRenderers/proceduralSpecs';

// Helper function to detect sofa type based on dimensions
const detectSofaType = (width, depth) => {
  // Check for sectional sofas first
  if (Math.abs(width - 2.74) < 0.1 && Math.abs(depth - 2.74) < 0.1) {
    return 'L-sectional';
  }
  if (Math.abs(width - 3.05) < 0.1 && Math.abs(depth - 2.13) < 0.1) {
    return 'U-sectional';
  }

  // Check for standard sizes
  if (width < 1.6) {
    return 'loveseat';
  } else if (width < 1.9) {
    return 'single';
  } else {
    return 'triple';
  }
};

export const getObjectType = (obj) => {
  if (!obj) return 'Unknown';

  // Prefer the explicit `type` field — it is the source-of-truth for
  // shape-rendering decisions and (unlike category key order) survives
  // Firestore round-trips intact. Multi-category objects like the L
  // corner cabinet (`{ 'corner-cabinet': true, 'lower-cabinet': true }`)
  // were falling through to whichever key happened to land first in
  // the deserialized map, which sometimes drew them as a plain
  // 36×36 lower cabinet.
  if (typeof obj.type === 'string' && obj.type.length > 0) {
    return obj.type.charAt(0).toUpperCase() + obj.type.slice(1);
  }

  if (obj.category) {
    const categories = Object.keys(obj.category);
    if (categories.length > 0) {
      return categories[0].charAt(0).toUpperCase() + categories[0].slice(1);
    }
  }

  return 'Unknown';
};

export const getObjectColor = (obj) => {
  // Check obj.type first (more specific), then fall back to category
  const type = obj.type || (obj.category ? Object.keys(obj.category)[0] : null);
  if (!type) return '#888888';

  const colorMap = {
    // Furniture
    table: '#8B4513', // Saddle brown
    chair: '#A0522D', // Sienna
    sofa: '#4169E1', // Royal blue
    bed: '#DC143C', // Crimson
    cabinet: '#6B4423', // Dark wood
    'corner-cabinet': '#6B4423', // Dark wood (matches lower-cabinet)
    'fridge-surround': '#6B4423', // Dark wood (side panels around the fridge)
    'laundry-surround': '#6B4423', // same panels, around a washer/dryer
    'oven-surround': '#6B4423', // Dark wood (side panels around the oven)
    'lower-cabinet': '#6B4423', // Dark wood
    'blank-panel': '#6B4423', // Dark wood — matches adjacent cabinets
    shelf: '#8B7355', // Burlywood
    bookshelf: '#654321', // Dark brown
    dresser: '#A0522D', // Sienna

    // Kitchen — both old (stove / range-hood / oven) and new (range /
    // vent-hood / wall-oven) keys map to the same colors so saved
    // structures from before the GLB-aligned type rename render the same.
    fridge: '#C0C0C0', // Silver
    refrigerator: '#C0C0C0', // Silver
    dishwasher: '#C0C0C0', // Silver — matches fridge
    oven: '#2F2F2F', // Dark gray
    'wall-oven': '#2F2F2F', // Dark gray
    stove: '#2F2F2F', // Dark gray (same as oven)
    range: '#2F2F2F', // Dark gray
    cooktop: '#2F2F2F', // Dark gray (a range's deck without the body)
    'range-hood': '#9CA3AF', // Light grey (stainless-ish)
    'vent-hood': '#9CA3AF', // Light grey (stainless-ish)
    washerdryer: '#FFFFFF', // White
    washerDryer: '#FFFFFF', // White

    // Storage
    storage: '#8B7355', // Burlywood
    countertop: '#F0F0F0', // Light gray

    // Electronics
    television: '#000000', // Black

    // Bathroom
    toilet: '#FFFFFF', // White
    bathtub: '#FFFFFF',
    'tub-shower': '#FFFFFF', // White — same as bathtub
    vanity: '#8B7355', // Burlywood (same as storage)
    sink: '#FFFFFF', // White
    'double-sink': '#FFFFFF', // White
    'bathroom-sink-faucet': '#C0C0C0', // Silver/Chrome
    'bathroom-tub-faucet': '#C0C0C0', // Silver/Chrome
    shower: '#888888', // Gray (all wall counts)
    'shower-door': '#E0E0E0', // Light gray
    'shower-controls': '#C0C0C0', // Silver/Chrome
    'shower-stem': '#C0C0C0', // Silver/Chrome

    // Stairs
    stairs: '#9CA3AF', // Light grey

    // Fireplace
    fireplace: '#8B4513', // Saddle brown (brick-like color)
    'wood-stove': '#2F2F2F', // Cast iron

    // Lighting
    'overhead-wall-light': '#FFD700', // Gold
    'wall-light': '#FFD700', // Gold (alternate)
    'ceiling-light': '#FFD700', // Gold
    walllight: '#FFD700', // Gold (alternate)
    ceilinglight: '#FFD700', // Gold (alternate)

    // Electrical (red for debug)
    switch: '#FF0000', // Red
    dimmer: '#FF0000', // Red
    '3way': '#FF0000', // Red
    electrical: '#FF0000', // Red

    // Default
    default: '#888888', // Gray
  };

  return colorMap[type] || colorMap.default;
};

// Draw object shape based on type
/**
 * Fill and stroke a plan outline stored in WORLD coordinates (`[{ x, z }]`)
 * in the object's drawing frame — the caller has already applied
 * `ctx.translate(center.x, center.z)` and `ctx.rotate(objectAngle)`, so each
 * vertex is taken from world (x, z) to the post-rotation local frame:
 *   localX = Δx·cos θ + Δz·sin θ,  localY = −Δx·sin θ + Δz·cos θ.
 * False (nothing drawn) without an outline or a centre.
 */
function drawWorldOutline(ctx, polyCorners, center, objectAngle) {
  if (
    !Array.isArray(polyCorners) ||
    polyCorners.length < 3 ||
    !center ||
    typeof center.x !== 'number' ||
    typeof center.z !== 'number'
  )
    return false;
  const cosA = Math.cos(objectAngle || 0);
  const sinA = Math.sin(objectAngle || 0);
  ctx.beginPath();
  polyCorners.forEach((p, i) => {
    const dx = p.x - center.x;
    const dz = p.z - center.z;
    const localX = dx * cosA + dz * sinA;
    const localY = -dx * sinA + dz * cosA;
    if (i === 0) ctx.moveTo(localX, localY);
    else ctx.lineTo(localX, localY);
  });
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  return true;
}

/** An angled end with an open side drawn by its cut outline; false otherwise. */
const drawAngledEnd = (ctx, o, objectAngle) =>
  !!o?.category?.angledEnd &&
  !!o.angledEndSide &&
  !!o.center &&
  !!o.dimensions &&
  drawWorldOutline(ctx, angledEndOutline(o), o.center, objectAngle);

export const drawObjectShape = (
  ctx,
  objectType,
  width,
  height,
  depth,
  scale,
  objectAngle = 0,
  attributes = {},
  fullObject = null, // Full object for accessing model metadata (e.g., faucet mounting points)
  { electricalMode = false } = {} // Electrical drawing: lights draw as symbols
) => {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;

  // On the electrical drawing a light is a SYMBOL, not a picture of a fixture:
  // it replaces the decorative shape entirely, at a size you can read at plan
  // zoom (a 4" recessed can is ~5 px across when drawn to scale).
  if (electricalMode) {
    const lightSymbol = lightSymbolOf(fullObject);
    if (lightSymbol) {
      drawLightSymbol(ctx, lightSymbol, scale, objectAngle, halfDepth);
      return;
    }
  }

  // A cabinet FILLER (lower/upper) is a blank false-panel slab — render it as a
  // plain rectangle with no door divider / knobs, matching the 'blank-panel'
  // look, instead of a tiny cabinet (which reads as clutter on sub-3" strips).
  // An OPEN SHELF (`category.openShelf`) is doorless too: the box with no
  // front, so the door divider and knobs would draw doors it does not have.
  const isDoorlessCabinet =
    !!fullObject?.category?.filler ||
    !!fullObject?.category?.openShelf ||
    !!fullObject?.category?.floatingShelves;

  // Electrical devices draw their plan symbol (Electrical mode only — the
  // normal plan filters them out before they reach here). Keyed off the
  // catalog rather than a case per type: as a hand-listed set of cases this
  // silently dropped `surface-outlet` to the default rectangle, which draws a
  // blank square with nothing thrown.
  if (isElectricalDeviceType(objectType)) {
    drawElectricalDeviceSymbol(ctx, objectType, scale, objectAngle);
    return;
  }

  switch (objectType.toLowerCase()) {
    case 'table':
      // Draw as rectangle
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      break;

    case 'chair':
      // Draw as smaller rectangle with a back
      const chairSeat = Math.min(width, depth) * 0.8;
      const chairBack = chairSeat * 0.1;
      ctx.fillRect(-chairSeat / 2, -chairSeat / 2, chairSeat, chairSeat);
      ctx.strokeRect(-chairSeat / 2, -chairSeat / 2, chairSeat, chairSeat);
      // Chair back (at the back of the chair, negative X direction)
      ctx.fillRect(
        -chairSeat / 2 - chairBack,
        -chairSeat / 2,
        chairBack,
        chairSeat
      );
      ctx.strokeRect(
        -chairSeat / 2 - chairBack,
        -chairSeat / 2,
        chairBack,
        chairSeat
      );
      break;

    case 'sofa':
      // Use SofaType attribute if available, otherwise detect based on dimensions
      const sofaType = attributes?.SofaType || detectSofaType(width, depth);

      if (sofaType === 'L-sectional') {
        // Draw L-shaped sectional sofa - matching U-sectional style
        const sectionWidth = width * 0.45; // Width of side section (more equal)
        const sectionDepth = depth * 0.45; // Depth of sections (more equal)
        const armWidth = width * 0.06;

        // Draw main L-shape (opening toward the front/positive X)
        ctx.beginPath();
        // Start from back left outer corner
        ctx.moveTo(-halfDepth, -halfWidth);
        // Go to back right outer
        ctx.lineTo(-halfDepth, halfWidth);
        // Go forward on right side
        ctx.lineTo(halfDepth, halfWidth);
        // Go left to create the L corner
        ctx.lineTo(halfDepth, halfWidth - sectionWidth);
        // Go back to create inner corner
        ctx.lineTo(-halfDepth + sectionDepth, halfWidth - sectionWidth);
        // Go up along the inner edge
        ctx.lineTo(-halfDepth + sectionDepth, -halfWidth);
        // Back to start
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Draw armrests and backrests on outer edges
        ctx.save();
        // Back armrest/backrest (along the back)
        ctx.fillRect(-halfDepth, -halfWidth, armWidth, width);
        ctx.strokeRect(-halfDepth, -halfWidth, armWidth, width);

        // Left side armrest
        ctx.fillRect(-halfDepth, -halfWidth, sectionDepth, armWidth);
        ctx.strokeRect(-halfDepth, -halfWidth, sectionDepth, armWidth);

        // Right side backrest (along the outer edge of the side section)
        ctx.fillRect(-halfDepth, halfWidth - armWidth, depth, armWidth);
        ctx.strokeRect(-halfDepth, halfWidth - armWidth, depth, armWidth);

        // Right/front armrest (on the extended right section)
        ctx.fillRect(
          halfDepth - armWidth,
          halfWidth - sectionWidth,
          armWidth,
          sectionWidth
        );
        ctx.strokeRect(
          halfDepth - armWidth,
          halfWidth - sectionWidth,
          armWidth,
          sectionWidth
        );

        ctx.restore();

        // Draw cushion lines
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.setLineDash([5, 5]);

        // Back section cushion divisions (evenly spaced)
        // Divide the back section into equal parts
        const backSectionLength = width - sectionWidth;
        const numBackCushions = 3;
        for (let i = 1; i < numBackCushions; i++) {
          const y = -halfWidth + (i * backSectionLength) / numBackCushions;
          ctx.beginPath();
          ctx.moveTo(-halfDepth + sectionDepth, y);
          ctx.lineTo(-halfDepth, y);
          ctx.stroke();
        }

        // Side section cushion divisions (evenly spaced)
        // Divide the side section into equal parts
        const sideSectionLength = depth - sectionDepth;
        const numSideCushions = 3; // Changed from 2 to 3 to get 2 division lines
        for (let i = 1; i < numSideCushions; i++) {
          const x =
            -halfDepth +
            sectionDepth +
            (i * sideSectionLength) / numSideCushions;
          ctx.beginPath();
          ctx.moveTo(x, halfWidth - sectionWidth);
          ctx.lineTo(x, halfWidth);
          ctx.stroke();
        }

        // Corner/junction cushion line (optional - connects the two sections)
        ctx.beginPath();
        ctx.moveTo(-halfDepth + sectionDepth, halfWidth - sectionWidth);
        ctx.lineTo(-halfDepth, halfWidth - sectionWidth);
        ctx.stroke();

        ctx.setLineDash([]);
        ctx.restore();
      } else if (sofaType === 'U-sectional') {
        // Draw U-shaped sectional sofa (3.05m x 2.13m)
        const sectionWidth = width * 0.3; // Adjust for rectangular shape
        const sectionDepth = depth * 0.4; // Depth of side sections
        const armWidth = width * 0.06;

        // Draw main U-shape (opening toward the front/positive X)
        ctx.beginPath();
        // Start from back left outer corner
        ctx.moveTo(-halfDepth, -halfWidth);
        // Go to back right outer
        ctx.lineTo(-halfDepth, halfWidth);
        // Go forward on right side
        ctx.lineTo(halfDepth, halfWidth);
        // Go left partway (front right)
        ctx.lineTo(halfDepth, halfWidth - sectionWidth);
        // Go back to create inner U
        ctx.lineTo(-halfDepth + sectionDepth, halfWidth - sectionWidth);
        // Go across to left inner
        ctx.lineTo(-halfDepth + sectionDepth, -halfWidth + sectionWidth);
        // Go forward on left inner
        ctx.lineTo(halfDepth, -halfWidth + sectionWidth);
        // Go to front left outer
        ctx.lineTo(halfDepth, -halfWidth);
        // Back to start
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Draw armrests on outer edges only
        ctx.save();
        // Back armrest (connecting the U at the back)
        ctx.fillRect(-halfDepth, -halfWidth, armWidth, width);
        ctx.strokeRect(-halfDepth, -halfWidth, armWidth, width);

        // Left side armrest
        ctx.fillRect(-halfDepth, -halfWidth, depth, armWidth);
        ctx.strokeRect(-halfDepth, -halfWidth, depth, armWidth);

        // Right side armrest
        ctx.fillRect(-halfDepth, halfWidth - armWidth, depth, armWidth);
        ctx.strokeRect(-halfDepth, halfWidth - armWidth, depth, armWidth);
        ctx.restore();

        // Draw cushion lines
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.setLineDash([5, 5]);

        // Back section cushion divisions (3 cushions across the back)
        const backCushionY1 = -halfWidth + width / 3;
        ctx.beginPath();
        ctx.moveTo(-halfDepth + sectionDepth, backCushionY1);
        ctx.lineTo(-halfDepth, backCushionY1);
        ctx.stroke();

        const backCushionY2 = -halfWidth + (2 * width) / 3;
        ctx.beginPath();
        ctx.moveTo(-halfDepth + sectionDepth, backCushionY2);
        ctx.lineTo(-halfDepth, backCushionY2);
        ctx.stroke();

        // Side section cushion divisions
        // Left side cushion
        const sideCushionX1 = 0;
        ctx.beginPath();
        ctx.moveTo(sideCushionX1, -halfWidth + sectionWidth);
        ctx.lineTo(sideCushionX1, -halfWidth);
        ctx.stroke();

        // Right side cushion
        ctx.beginPath();
        ctx.moveTo(sideCushionX1, halfWidth - sectionWidth);
        ctx.lineTo(sideCushionX1, halfWidth);
        ctx.stroke();

        ctx.setLineDash([]);
        ctx.restore();
      } else {
        // Standard sofa drawing
        // Draw main body as rounded rectangle
        drawRoundedRect(ctx, -halfDepth, -halfWidth, depth, width, 0.1);

        // Draw armrests with curved edges
        const armrestWidth = width * 0.15;
        const armrestDepth = depth; // Full depth to match sofa
        const armrestX = -halfDepth; // Start at same position as sofa

        // Left armrest with curved outer edge
        ctx.save();
        ctx.beginPath();
        // Create a path that follows the sofa's curved edge
        const r = 0.1 * Math.min(width, depth); // Same radius as sofa

        // Left armrest path
        ctx.moveTo(armrestX, -halfWidth + armrestWidth);
        ctx.lineTo(armrestX + armrestDepth, -halfWidth + armrestWidth);
        ctx.lineTo(armrestX + armrestDepth, -halfWidth + r);
        // Top right curve
        ctx.quadraticCurveTo(
          armrestX + armrestDepth,
          -halfWidth,
          armrestX + armrestDepth - r,
          -halfWidth
        );
        ctx.lineTo(armrestX + r, -halfWidth);
        // Top left curve
        ctx.quadraticCurveTo(armrestX, -halfWidth, armrestX, -halfWidth + r);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Right armrest with curved outer edge
        ctx.beginPath();
        ctx.moveTo(armrestX, halfWidth - armrestWidth);
        ctx.lineTo(armrestX + armrestDepth, halfWidth - armrestWidth);
        ctx.lineTo(armrestX + armrestDepth, halfWidth - r);
        // Bottom right curve
        ctx.quadraticCurveTo(
          armrestX + armrestDepth,
          halfWidth,
          armrestX + armrestDepth - r,
          halfWidth
        );
        ctx.lineTo(armrestX + r, halfWidth);
        // Bottom left curve
        ctx.quadraticCurveTo(armrestX, halfWidth, armrestX, halfWidth - r);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();

        // Draw back cushions
        ctx.save();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)'; // Light overlay for cushions
        const cushionGap = width * 0.02; // Consistent gap size
        const availableWidth = width - armrestWidth * 2 - cushionGap * 2; // Space between armrests minus gaps

        // Determine cushion count based on sofa proportions
        const aspectRatio = width / depth;
        let cushionCount;
        if (aspectRatio <= 1.5) {
          // Square or nearly square sofa - use 1 cushion
          cushionCount = 1;
        } else {
          // Wider sofa - use multiple cushions
          cushionCount = Math.max(2, Math.floor(width / (depth * 0.8)));
        }

        const totalCushionGaps = cushionGap * (cushionCount - 1); // Gaps between cushions
        const cushionWidth = (availableWidth - totalCushionGaps) / cushionCount;
        const cushionDepth = depth * 0.25; // Back cushions are thinner
        const backPosition = -halfDepth; // Position at the back

        for (let i = 0; i < cushionCount; i++) {
          const cushionX =
            -halfWidth +
            armrestWidth +
            cushionGap +
            i * (cushionWidth + cushionGap);
          ctx.fillRect(backPosition, cushionX, cushionDepth, cushionWidth);
          ctx.strokeRect(backPosition, cushionX, cushionDepth, cushionWidth);
        }
        ctx.restore();
      }
      break;

    case 'bed':
      // Draw as rectangle with pillow area
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // Pillow area at the head of the bed (back, opposite to direction)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.fillRect(-halfDepth, -halfWidth, depth * 0.3, width);
      break;

    case 'fridge':
    case 'refrigerator': {
      // Draw as rectangle with handle line
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // Handle
      ctx.beginPath();
      ctx.moveTo(halfDepth - 0.05, -halfWidth * 0.5);
      ctx.lineTo(halfDepth - 0.05, halfWidth * 0.5);
      ctx.stroke();
      // Add "R" text - draw after rotation to keep text upright
      ctx.save();
      ctx.rotate(-objectAngle); // Undo object rotation to keep text upright
      ctx.fillStyle = '#000000';
      // Scale font inversely to canvas scale to maintain consistent size
      const fontSize = 12 / scale; // 12px target size, divided by canvas scale
      ctx.font = `300 ${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('R', 0, 0);
      ctx.restore();
      break;
    }

    case 'dishwasher': {
      // Force the dishwasher fill — drawObject's fillStyle can
      // otherwise inherit a near-identical grey to the countertop
      // beneath it and the rectangle becomes invisible in
      // thumbnails. We always paint the dishwasher in its own color
      // so it reads as a distinct rectangle on the floor plan.
      ctx.save();
      ctx.fillStyle = getObjectColor({ category: { dishwasher: true } });
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.restore();
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // Handle
      ctx.beginPath();
      ctx.moveTo(halfDepth - 0.05, -halfWidth * 0.5);
      ctx.lineTo(halfDepth - 0.05, halfWidth * 0.5);
      ctx.stroke();
      // Add "DW" text - draw after rotation to keep text upright
      ctx.save();
      ctx.rotate(-objectAngle); // Undo object rotation to keep text upright
      ctx.fillStyle = '#000000';
      // Scale font inversely to canvas scale to maintain consistent size
      const fontSize = 11 / scale; // 11px target size, divided by canvas scale
      ctx.font = `300 ${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('DW', 0, 0);
      ctx.restore();
      break;
    }

    case 'washerdryer':
    case 'washerDryer': {
      // Draw as rectangle with handle line
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // Handle
      ctx.beginPath();
      ctx.moveTo(halfDepth - 0.05, -halfWidth * 0.5);
      ctx.lineTo(halfDepth - 0.05, halfWidth * 0.5);
      ctx.stroke();
      // Add "W/D" text - draw after rotation to keep text upright
      ctx.save();
      ctx.rotate(-objectAngle); // Undo object rotation to keep text upright
      ctx.fillStyle = '#000000';
      // Scale font inversely to canvas scale to maintain consistent size
      const fontSize = 10 / scale; // 10px target size, divided by canvas scale
      ctx.font = `300 ${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('W/D', 0, 0);
      ctx.restore();
      break;
    }

    case 'oven':
    case 'wall-oven':
      // Draw as rectangle with handle line - TRANSLUCENT FOR DEBUG
      ctx.save();
      ctx.globalAlpha = 0.3; // Translucent for debugging
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.restore();
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);

      // Handle that protrudes from the front
      const handleProtrusion = 0.075; // How far the handle extends out (reduced by half)
      const handleWidth = width * 0.85; // Handle is 85% of oven width (increased from 70%)
      const curveRadius = handleProtrusion * 0.3; // Radius for the curved corners

      ctx.beginPath();
      // Start from one side of the handle on the oven face
      ctx.moveTo(halfDepth, -handleWidth / 2);
      // Go out to the handle bar with a curve
      ctx.lineTo(halfDepth + handleProtrusion - curveRadius, -handleWidth / 2);
      ctx.quadraticCurveTo(
        halfDepth + handleProtrusion,
        -handleWidth / 2,
        halfDepth + handleProtrusion,
        -handleWidth / 2 + curveRadius
      );
      // Draw the handle bar
      ctx.lineTo(halfDepth + handleProtrusion, handleWidth / 2 - curveRadius);
      // Curve at the other end
      ctx.quadraticCurveTo(
        halfDepth + handleProtrusion,
        handleWidth / 2,
        halfDepth + handleProtrusion - curveRadius,
        handleWidth / 2
      );
      // Come back to the oven face
      ctx.lineTo(halfDepth, handleWidth / 2);
      ctx.stroke();
      break;

    case 'stove':
    case 'range':
    case 'cooktop':
      // Store original fill color
      const originalFillColor = ctx.fillStyle;

      // Draw as rectangle with specific stove color - TRANSLUCENT FOR DEBUG
      ctx.save();
      ctx.fillStyle =
        getObjectColor({ category: { range: true } }) ||
        getObjectColor({ category: { stove: true } }) ||
        '#2F2F2F';
      ctx.globalAlpha = 0.3; // Translucent for debugging
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.restore();
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);

      // Restore original fill
      ctx.fillStyle = originalFillColor;

      // Determine number of burners based on aspect ratio
      const stoveAspectRatio = Math.max(width, depth) / Math.min(width, depth);
      let burnerRows, burnerCols;

      if (stoveAspectRatio >= 4) {
        // 2:4 ratio or more - 8 burners (2x4 or 4x2)
        burnerRows = width > depth ? 2 : 4;
        burnerCols = width > depth ? 4 : 2;
      } else if (stoveAspectRatio >= 3) {
        // 2:3 ratio or more - 6 burners (2x3 or 3x2)
        burnerRows = width > depth ? 2 : 3;
        burnerCols = width > depth ? 3 : 2;
      } else {
        // Roughly square - 4 burners (2x2)
        burnerRows = 2;
        burnerCols = 2;
      }

      // Calculate burner spacing
      const burnerRadius = Math.min(width, depth) * 0.18; // Bigger burners to fill more space
      // Increase spacing to prevent overlap
      const xSpacing = depth / (burnerCols + 0.5);
      const ySpacing = width / (burnerRows + 0.5);

      // Draw burners with contrasting color
      ctx.save();
      ctx.strokeStyle = '#000000'; // Black outline
      ctx.lineWidth = Math.max(0.02, ctx.lineWidth * 0.5); // Scale line width properly

      for (let row = 0; row < burnerRows; row++) {
        for (let col = 0; col < burnerCols; col++) {
          // Center the burners better with adjusted spacing
          const x = -halfDepth + (col + 0.75) * xSpacing;
          const y = -halfWidth + (row + 0.75) * ySpacing;

          // Outer circle
          ctx.beginPath();
          ctx.arc(x, y, burnerRadius, 0, Math.PI * 2);
          ctx.stroke();

          // Inner circle
          ctx.beginPath();
          ctx.arc(x, y, burnerRadius * 0.5, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      ctx.restore();
      break;

    case 'cabinet':
    case 'dresser':
      // Draw as rectangle with door lines
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // Door divider
      ctx.beginPath();
      ctx.moveTo(0, -halfWidth);
      ctx.lineTo(0, halfWidth);
      ctx.stroke();
      break;

    case 'storage':
    case 'storage-furniture':
      // Draw as simple rectangle without door lines - TRANSLUCENT FOR DEBUG
      ctx.save();
      ctx.globalAlpha = 0.3; // Translucent for debugging
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.restore();
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      break;

    case 'lower-cabinet':
      // an angled end draws its cut outline (`angledEnds.js`)
      if (drawAngledEnd(ctx, fullObject, objectAngle)) break;
      // Draw as rectangle with door lines and knobs
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      if (!isDoorlessCabinet) {
        // Door divider
        ctx.beginPath();
        ctx.moveTo(0, -halfWidth);
        ctx.lineTo(0, halfWidth);
        ctx.stroke();
        // Knobs
        ctx.beginPath();
        ctx.arc(
          -halfDepth * 0.3,
          -halfWidth * 0.3,
          width * 0.02,
          0,
          Math.PI * 2
        );
        ctx.arc(
          -halfDepth * 0.3,
          halfWidth * 0.3,
          width * 0.02,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
      break;

    case 'corner-cabinet': {
      // FRAME: the caller has applied `ctx.rotate(objectAngle)`, so canvas x is
      // the unit's DEPTH axis (-halfDepth = the wall, +halfDepth = the room)
      // and canvas y is the NEGATIVE of its along-wall axis — local +X runs
      // from +halfWidth toward -halfWidth. `cornerCabinetFace` answers along
      // local +X, the same answer the 3D renderer and the elevation draw from,
      // so map it through `faceY` and nothing here decides a side by itself.
      // The unit's own dimensions win; `attributes` is the older call path
      // that carried the same metadata (`cornerBox` / `cornerVariant`).
      const cornerDims = {
        ...(attributes || {}),
        ...(fullObject?.dimensions || {}),
      };
      const cornerFace = cornerCabinetFace(cornerDims, width);
      const faceY = (localX) => halfWidth - localX;

      // Single-sided (blind) variant — a straight cabinet that runs to the
      // corner, with the perpendicular run standing across `blindDepth` of its
      // face. One door, on the part you can reach.
      const variant = cornerDims.cornerVariant;
      if (variant === 'single-sided') {
        ctx.fillRect(-halfDepth, -halfWidth, depth, width);
        ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
        if (cornerFace) {
          // Seam between the blind span's blank face and the door.
          if (cornerFace.blindWidth > 0) {
            const seamY = faceY(
              cornerFace.blindAtLowX
                ? cornerFace.blindWidth
                : width - cornerFace.blindWidth
            );
            ctx.beginPath();
            ctx.moveTo(-halfDepth, seamY);
            ctx.lineTo(halfDepth, seamY);
            ctx.stroke();
          }
          // One knob, on the door's reachable edge — away from the blind span.
          const knobInset = 4 * 0.0254;
          const knobY = faceY(
            cornerFace.pullAtHighX
              ? cornerFace.doorEnd - knobInset
              : cornerFace.doorStart + knobInset
          );
          ctx.beginPath();
          ctx.arc(-halfDepth * 0.3, knobY, width * 0.02, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }

      // L-shape drawn as two overlapping fillRects. Convention after
      // accounting for objectAngle's rotation: the wall corner of the
      // room sits at canvas (-halfDepth, +halfWidth); local +Y is the
      // OPPOSITE of wallA's along-direction-from-corner. wallB leg
      // therefore lives at the +Y end of the bounding box, not the -Y end.
      // The leg's DEPTH into the room — the same number the face split uses for
      // the blind span, since a corner is one run built at one depth.
      const cornerBox = cornerFace ? cornerFace.blindWidth : 24 * 0.0254;
      const innerFrontDepth = -halfDepth + cornerBox; // front face of wallA leg
      const innerWallBEdge = halfWidth - cornerBox; // inward edge of wallB leg

      // wallA leg (along the wallA face): cornerBox deep, full width.
      ctx.fillRect(-halfDepth, -halfWidth, cornerBox, width);
      // wallB leg (along the wallB face): full depth, cornerBox wide,
      // anchored at the +Y end of the bounding box.
      ctx.fillRect(-halfDepth, innerWallBEdge, depth, cornerBox);

      // L outline (CCW from the wall corner of the room).
      ctx.beginPath();
      ctx.moveTo(-halfDepth, halfWidth); // 1: wall corner of room
      ctx.lineTo(-halfDepth, -halfWidth); // 2: end of wallA leg
      ctx.lineTo(innerFrontDepth, -halfWidth); // 3: front-end of wallA leg
      ctx.lineTo(innerFrontDepth, innerWallBEdge); // 4: inner L corner
      ctx.lineTo(halfDepth, innerWallBEdge); // 5: front-end of wallB leg
      ctx.lineTo(halfDepth, halfWidth); // 6: end of wallB leg
      ctx.closePath();
      ctx.stroke();

      // Knob on the wallA leg's front face, 4" in from the door's reachable
      // edge — the end away from the corner, which is where the 3D renderer
      // and the elevation put the pull.
      const handleInset = 4 * 0.0254;
      if (cornerFace) {
        const knobLocalX = cornerFace.pullAtHighX
          ? cornerFace.doorEnd - handleInset
          : cornerFace.doorStart + handleInset;
        ctx.beginPath();
        ctx.arc(
          innerFrontDepth,
          faceY(knobLocalX),
          Math.min(width, depth) * 0.025,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
      break;
    }

    case 'blank-panel': {
      // Decorative filler covering the corner-side gap on a sink wall
      // when a single-sided corner cabinet sits on the perpendicular
      // wall. The 3D body is intentionally thin (~1"), but in the 2D
      // top-down / UV floorplan view it should read like the rest of
      // the cabinet run — draw a full 24"-deep box anchored at the
      // wall surface so it lines up with the adjacent lower cabinets.
      //
      // A free-standing island back panel is the opposite case: a thin
      // decorative panel on the island's OUTWARD (seating) face. The
      // 24" box would jut into the seating area and make the island read
      // ~48" deep (24" cabinets + 24" panel box), so draw the
      // free-standing panel at its true thickness — the countertop's
      // seating overhang already defines the island's footprint.
      const isFreestandingBack = !!(
        fullObject?.category?.freestanding ||
        fullObject?.dimensions?.freestanding
      );
      const cabinetDepth = isFreestandingBack ? depth : 24 * 0.0254;
      // Object center sits ~½" from the wall surface (PANEL_DEPTH / 2),
      // so the wall surface is at canvas X = -halfDepth. Draw the
      // box from there forward.
      ctx.fillRect(-halfDepth, -halfWidth, cabinetDepth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, cabinetDepth, width);
      break;
    }

    case 'upper-cabinet':
      // an angled end draws its cut outline (`angledEnds.js`)
      if (drawAngledEnd(ctx, fullObject, objectAngle)) break;
      // Draw as rectangle with door lines (wall-mounted style)
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      if (!isDoorlessCabinet) {
        // Door divider
        ctx.beginPath();
        ctx.moveTo(0, -halfWidth);
        ctx.lineTo(0, halfWidth);
        ctx.stroke();
        // Knobs (closer to center for upper cabinets)
        ctx.beginPath();
        ctx.arc(
          halfDepth * 0.5,
          -halfWidth * 0.2,
          width * 0.02,
          0,
          Math.PI * 2
        );
        ctx.arc(halfDepth * 0.5, halfWidth * 0.2, width * 0.02, 0, Math.PI * 2);
        ctx.fill();
      }
      break;

    case 'oven-surround':
    case 'laundry-surround':
    case 'fridge-surround': {
      // Draw only the two side panels — the fridge inside the bounding
      // box stays visible (same shape for an oven surround). canvas X =
      // depth axis, canvas Y = width
      // axis. The panels sit at the +Y and -Y edges of the bounding
      // box, full depth.
      const sideThickness =
        fullObject?.dimensions?.sidePanelThickness ??
        attributes?.sidePanelThickness ??
        0.5 * 0.0254;
      ctx.fillRect(-halfDepth, -halfWidth, depth, sideThickness);
      ctx.fillRect(-halfDepth, halfWidth - sideThickness, depth, sideThickness);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, sideThickness);
      ctx.strokeRect(
        -halfDepth,
        halfWidth - sideThickness,
        depth,
        sideThickness
      );
      break;
    }

    case 'range-hood':
    case 'vent-hood': {
      // Plain rectangle outline with a thin strip near the wall side
      // showing the hood's vent slots in plan.
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      const slotInset = depth * 0.15;
      const slotY1 = -halfWidth + width * 0.2;
      const slotY2 = halfWidth - width * 0.2;
      ctx.beginPath();
      ctx.moveTo(-halfDepth + slotInset, slotY1);
      ctx.lineTo(halfDepth - slotInset, slotY1);
      ctx.moveTo(-halfDepth + slotInset, slotY2);
      ctx.lineTo(halfDepth - slotInset, slotY2);
      ctx.stroke();
      break;
    }

    case 'shelf':
    case 'bookshelf':
      // Draw as rectangle with shelf lines
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // Shelves
      for (let i = 1; i < 4; i++) {
        const y = -halfWidth + (width * i) / 4;
        ctx.beginPath();
        ctx.moveTo(-halfDepth, y);
        ctx.lineTo(halfDepth, y);
        ctx.stroke();
      }
      break;

    case 'television':
      // Draw as rectangle representing a TV/monitor
      // Draw main TV screen as a filled rectangle
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);

      // Draw inner screen area to show it's a display
      const bezelSize = Math.min(width, depth) * 0.1;
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'; // Darker center to indicate screen
      ctx.fillRect(
        -halfDepth + bezelSize,
        -halfWidth + bezelSize,
        depth - 2 * bezelSize,
        width - 2 * bezelSize
      );
      ctx.restore();
      break;

    case 'sink':
    case 'double-sink': {
      // Outer rectangle (sink fixture body / vanity counter).
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);

      // Each basin is nominally 24" wide centered on its lateral offset
      // (from the cabinet center along the wall direction). Pipeline emits
      // `sinkLateralOffsets` for double-sinks (carrying per-basin lateral
      // positions, including off-center positions from the clearance slide).
      // Single-sink data doesn't usually carry the field; default to one
      // centered basin. Legacy double-sinks without the field default to
      // symmetric.
      //
      // The basin is clamped per-basin to fit inside the cabinet outline
      // (with `sinkInset` margin), so narrow cabinets (e.g. shrunk down to
      // 14") render a basin that fits rather than a 24" basin overflowing
      // the vanity body.
      const BASIN_WIDTH = 0.6096; // 24 inches
      const sinkInset = Math.min(width, depth) * 0.1;
      const basinHalfW = BASIN_WIDTH / 2;
      let offsets;
      if (
        Array.isArray(fullObject?.sinkLateralOffsets) &&
        fullObject.sinkLateralOffsets.length > 0
      ) {
        offsets = fullObject.sinkLateralOffsets;
      } else if (
        objectType.toLowerCase() === 'sink' &&
        Number.isFinite(fullObject?.clearanceOffset) &&
        fullObject.clearanceOffset !== 0
      ) {
        // Single sink whose basin clearance was slid laterally during S6
        // expansion — `clearanceOffset` is the only basin-shift signal on
        // the floor-plan fixture (no `sinkLateralOffsets` array). Use it so
        // the thumbnail basin matches where the pipeline placed it.
        offsets = [fullObject.clearanceOffset];
      } else if (objectType.toLowerCase() === 'double-sink') {
        offsets = [-width / 4, width / 4];
      } else {
        offsets = [0];
      }
      for (const offset of offsets) {
        // Pipeline `sinkLateralOffsets` are signed along +wallDir (toward
        // wall.end). The renderer's local frame has +Y mapped to -wallDir
        // (because objectAngle = atan2(wallNormal.x, wallNormal.z) puts
        // local +X along wallNormal, so 90° from that is local +Y →
        // -wallDir). Negate the offset to land basins on the correct side.
        const y = -offset;
        const availableHalfW = halfWidth - Math.abs(offset) - sinkInset;
        const drawnHalfW = Math.max(0, Math.min(basinHalfW, availableHalfW));
        if (drawnHalfW <= 0) continue;
        ctx.strokeRect(
          -halfDepth + sinkInset,
          y - drawnHalfW,
          depth - 2 * sinkInset,
          drawnHalfW * 2
        );
        // Faucet mount indicator behind each basin.
        const faucetRadius = Math.min(drawnHalfW * 2, depth) * 0.06;
        ctx.beginPath();
        ctx.arc(-halfDepth * 0.6, y, faucetRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      break;
    }

    case 'kitchen':
    case 'kitchen-sink': {
      // Kitchen sink — large single-bowl basin with rounded interior,
      // faucet hole centered behind the bowl, drain circle in the
      // middle. Larger usable basin than the bathroom undermount (kitchen
      // sinks fill more of their cabinet cutout).
      const ksOuterInset = Math.min(width, depth) * 0.04;
      const ksRadius = Math.min(width, depth) * 0.08;
      // Outer countertop cutout
      const outerX = -halfDepth + ksOuterInset;
      const outerY = -halfWidth + ksOuterInset;
      const outerW = depth - 2 * ksOuterInset;
      const outerH = width - 2 * ksOuterInset;
      if (typeof ctx.roundRect === 'function') {
        ctx.beginPath();
        ctx.roundRect(outerX, outerY, outerW, outerH, ksRadius);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.fillRect(outerX, outerY, outerW, outerH);
        ctx.strokeRect(outerX, outerY, outerW, outerH);
      }
      // Inner basin (the deep bowl) — or, for a double-bowl sink
      // (`basinCount: 2`), two bowls either side of the divider, each with
      // its drain. The width runs along canvas Y here.
      const ksBasinInset = Math.min(width, depth) * 0.12;
      const basinX = -halfDepth + ksBasinInset;
      const basinW = depth - 2 * ksBasinInset;
      const ksDrain = Math.min(width, depth) * 0.05;
      const bowls = kitchenSinkBowls(
        { width: width - 2 * ksBasinInset, cx: 0 },
        fullObject?.basinCount
      );
      for (const bowl of bowls) {
        const basinY = bowl.cx - bowl.width / 2;
        if (typeof ctx.roundRect === 'function') {
          ctx.beginPath();
          ctx.roundRect(basinX, basinY, basinW, bowl.width, ksRadius * 0.6);
          ctx.stroke();
        } else {
          ctx.strokeRect(basinX, basinY, basinW, bowl.width);
        }
        ctx.beginPath();
        ctx.arc(0, bowl.cx, ksDrain, 0, Math.PI * 2);
        ctx.stroke();
      }
      // Faucet hole — centered along width (left-right), behind the bowl
      // (negative X = back side toward wall)
      const ksFaucet = Math.min(width, depth) * 0.07;
      ctx.beginPath();
      ctx.arc(-halfDepth + ksOuterInset / 2, 0, ksFaucet, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      break;
    }

    case 'undermount':
    case 'vessel':
    case 'dropin':
      // Draw as elliptical basin (more common for counter sinks)
      // Depth along X (front-back), width along Y (left-right) — matches drawObjectShape convention
      ctx.beginPath();
      ctx.ellipse(0, 0, halfDepth * 0.9, halfWidth * 0.9, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Draw inner basin outline
      ctx.beginPath();
      ctx.ellipse(0, 0, halfDepth * 0.7, halfWidth * 0.7, 0, 0, Math.PI * 2);
      ctx.stroke();

      // Draw faucet position (small circle at back — negative X direction)
      const counterSinkFaucetRadius = Math.min(width, depth) * 0.06;
      ctx.beginPath();
      ctx.arc(-halfDepth * 0.6, 0, counterSinkFaucetRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Add type-specific indicator
      if (objectType === 'vessel') {
        // Vessel sink - add inner circle to show it sits on top
        ctx.beginPath();
        ctx.arc(0, 0, Math.min(halfWidth, halfDepth) * 0.5, 0, Math.PI * 2);
        ctx.stroke();
      }
      // Note: dropin doesn't need a rim indicator — just the ellipse is fine
      break;

    case 'bathroom-sink-faucet':
      // Draw faucet - same style as sink faucet but scaled up
      // (faucet is much smaller object: 0.1m vs sink 0.5m, so need bigger proportions)
      // IMPORTANT: The circles represent the mounting point, which should be 2" behind sink back edge
      const bathroomFaucetRadius = Math.min(width, depth) * 0.35; // Scaled up from 0.08

      // Get mounting point offset from stored data
      // This is the offset from the faucet center to the mounting point in meters
      // The mounting point is where the faucet mounts to the countertop (2" behind sink back edge)
      let mountingPointOffsetZ = 0; // Offset in meters (Z direction = depth in 2D)

      if (fullObject && fullObject.mountingPointOffset) {
        // Use the stored mounting point offset calculated during placement
        mountingPointOffsetZ = fullObject.mountingPointOffset.z || 0;
      }

      // Faucet base circle at mounting point location
      ctx.beginPath();
      ctx.arc(0, mountingPointOffsetZ, bathroomFaucetRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Faucet spout extending forward (toward positive X) - LONGER & BIGGER spout
      const bathroomSpoutLength = depth * 0.8; // LONGER: was 0.6 (sink uses 0.35)
      const bathroomSpoutThickness = bathroomFaucetRadius * 0.8; // BIGGER: was 0.4
      ctx.fillRect(
        0, // Start at mounting point X
        mountingPointOffsetZ - bathroomSpoutThickness / 2, // Position at mounting point Z
        bathroomSpoutLength,
        bathroomSpoutThickness
      );
      ctx.strokeRect(
        0, // Start at mounting point X
        mountingPointOffsetZ - bathroomSpoutThickness / 2, // Position at mounting point Z
        bathroomSpoutLength,
        bathroomSpoutThickness
      );

      // Draw handles on either side of faucet - MUCH WIDER spacing
      // These circles represent the mounting point at (0, mountingPointOffsetZ ± offset)
      const bathroomHandleOffset = width * 0.5; // MUCH WIDER: was 0.45 (sink uses 0.25)
      const bathroomHandleRadius = bathroomFaucetRadius * 0.7;

      // Left handle
      ctx.beginPath();
      ctx.arc(
        0, // At mounting point X
        mountingPointOffsetZ - bathroomHandleOffset, // Offset from mounting point Z
        bathroomHandleRadius,
        0,
        Math.PI * 2
      );
      ctx.stroke();

      // Right handle
      ctx.beginPath();
      ctx.arc(
        0, // At mounting point X
        mountingPointOffsetZ + bathroomHandleOffset, // Offset from mounting point Z
        bathroomHandleRadius,
        0,
        Math.PI * 2
      );
      ctx.stroke();
      break;

    case 'toilet':
      // Draw bowl as oval with proportions length x width
      ctx.beginPath();
      ctx.save();
      ctx.scale(1, width / depth); // Scale to make it depth x width (length x width)
      ctx.arc(0, 0, halfDepth, 0, Math.PI * 2);
      ctx.restore();
      ctx.fill();
      ctx.stroke();

      // Tank as rectangle: width x (length/5), positioned at back of oval
      const tankDepth = depth / 5;
      const ovalBackPosition = -halfDepth; // Back edge of oval (negative direction)
      ctx.fillRect(ovalBackPosition, -halfWidth, tankDepth, width);
      ctx.strokeRect(ovalBackPosition, -halfWidth, tankDepth, width);
      break;
    case 'tub-support-box':
    case 'bathtub': {
      const surroundPoly = fullObject?.['tub-surround-polygon'];
      const hasPolygonSurround =
        fullObject?.usePolygonSurround === true &&
        surroundPoly?.points?.length >= 3 &&
        fullObject?.center;

      if (hasPolygonSurround) {
        // Polygon surround tub: draw polygon-based shape with layered basin
        // Use -rotation.y for 2D angle (matches debug page convention:
        // 3D rotation.y = -visualAngle, so 2D angle = -rotation.y)
        const I2M = 0.0254;
        const polyAngle = -(fullObject.rotation?.y || 0);
        const cosR = Math.cos(polyAngle);
        const sinR = Math.sin(polyAngle);
        const pts = surroundPoly.points;

        const toLocal = (px, pz) => ({
          x: px * I2M * cosR - pz * I2M * sinR,
          z: px * I2M * sinR + pz * I2M * cosR,
        });

        const centroid = pts.reduce(
          (acc, p) => ({
            x: acc.x + p.x / pts.length,
            z: acc.z + p.z / pts.length,
          }),
          { x: 0, z: 0 }
        );

        const tracePolyLocal = (points) => {
          ctx.beginPath();
          for (let i = 0; i < points.length; i++) {
            const lp = toLocal(points[i].x, points[i].z);
            if (i === 0) ctx.moveTo(lp.x, lp.z);
            else ctx.lineTo(lp.x, lp.z);
          }
          ctx.closePath();
        };

        const insetPts = (factor) =>
          pts.map((p) => ({
            x: centroid.x + (p.x - centroid.x) * factor,
            z: centroid.z + (p.z - centroid.z) * factor,
          }));

        const savedJoin = ctx.lineJoin;
        ctx.lineJoin = 'miter';

        // Outer surround
        const prevFill = ctx.fillStyle;
        const prevStroke = ctx.strokeStyle;
        const prevLW = ctx.lineWidth;
        ctx.fillStyle = '#c8c8c8';
        tracePolyLocal(pts);
        ctx.fill();
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 3.5 / (scale || 1);
        tracePolyLocal(pts);
        ctx.stroke();

        // The basin. A catalog corner tub IS its polygon, so the basin is
        // the polygon inset. A deck cut to the bay (`outline: 'deck'`) holds
        // a rectangular basin that is smaller than the deck and sits off its
        // centre, and the tub carries that rectangle as its `tub-cutout` in
        // the same frame — draw the hole the deck actually has.
        const cutoutPts =
          surroundPoly.metadata?.outline === 'deck' &&
          fullObject?.['tub-cutout']?.points?.length >= 3
            ? fullObject['tub-cutout'].points.map((p) => ({
                x: p.x - (surroundPoly.centroid?.x || 0),
                z: p.z - (surroundPoly.centroid?.z || 0),
              }))
            : null;
        const basinCentroid = cutoutPts
          ? cutoutPts.reduce(
              (acc, p) => ({
                x: acc.x + p.x / cutoutPts.length,
                z: acc.z + p.z / cutoutPts.length,
              }),
              { x: 0, z: 0 }
            )
          : centroid;
        const basinAt = (factor) =>
          cutoutPts
            ? cutoutPts.map((p) => ({
                x: basinCentroid.x + (p.x - basinCentroid.x) * factor,
                z: basinCentroid.z + (p.z - basinCentroid.z) * factor,
              }))
            : insetPts(factor);

        // Basin (the cutout, or the polygon inset 0.94)
        ctx.fillStyle = '#7a7a7a';
        tracePolyLocal(basinAt(cutoutPts ? 1 : 0.94));
        ctx.fill();
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 3.5 / (scale || 1);
        tracePolyLocal(basinAt(cutoutPts ? 1 : 0.94));
        ctx.stroke();

        // Inner basin (inset 0.88)
        ctx.fillStyle = '#7a7a7a';
        tracePolyLocal(basinAt(cutoutPts ? 0.92 : 0.88));
        ctx.fill();
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 3 / (scale || 1);
        tracePolyLocal(basinAt(cutoutPts ? 0.92 : 0.88));
        ctx.stroke();

        // Drain at the basin's centre
        const drainPos = toLocal(basinCentroid.x, basinCentroid.z);
        const cornerDrainR = Math.min(width, depth) * 0.06;
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 2 / (scale || 1);
        ctx.beginPath();
        ctx.arc(drainPos.x, drainPos.z, cornerDrainR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.lineWidth = 1.5 / (scale || 1);
        ctx.beginPath();
        ctx.arc(drainPos.x, drainPos.z, cornerDrainR * 0.5, 0, Math.PI * 2);
        ctx.stroke();

        ctx.lineJoin = savedJoin;
        ctx.fillStyle = prevFill;
        ctx.strokeStyle = prevStroke;
        ctx.lineWidth = prevLW;
      } else {
        // Standard rectangular bathtub
        drawRoundedRect(ctx, -halfDepth, -halfWidth, depth, width, 0.15);

        const tubInset = Math.min(width, depth) * 0.08;
        ctx.beginPath();
        drawRoundedRectPath(
          ctx,
          -halfDepth + tubInset,
          -halfWidth + tubInset,
          depth - 2 * tubInset,
          width - 2 * tubInset,
          0.15
        );
        ctx.stroke();

        const tubDrainSize = Math.min(width, depth) * 0.08;
        const drainX = width > depth ? 0 : halfDepth * 0.7;
        const drainY = width > depth ? -halfWidth * 0.7 : 0;
        ctx.beginPath();
        ctx.arc(drainX, drainY, tubDrainSize, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(drainX, drainY, tubDrainSize * 0.5, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    }

    case 'tub-shower': {
      // Same shape as a bathtub, plus a door line along the front edge
      // (front = +depth direction, into the room; wall is at -halfDepth).
      drawRoundedRect(ctx, -halfDepth, -halfWidth, depth, width, 0.15);

      const tsInset = Math.min(width, depth) * 0.08;
      ctx.beginPath();
      drawRoundedRectPath(
        ctx,
        -halfDepth + tsInset,
        -halfWidth + tsInset,
        depth - 2 * tsInset,
        width - 2 * tsInset,
        0.15
      );
      ctx.stroke();

      // Drain on the short (depth) axis, same convention as bathtub
      const tsDrain = Math.min(width, depth) * 0.08;
      const tsDrainX = width > depth ? 0 : halfDepth * 0.7;
      const tsDrainY = width > depth ? -halfWidth * 0.7 : 0;
      ctx.beginPath();
      ctx.arc(tsDrainX, tsDrainY, tsDrain, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(tsDrainX, tsDrainY, tsDrain * 0.5, 0, Math.PI * 2);
      ctx.stroke();

      // Glass door line across the full open front of the alcove.
      // Offset slightly inward from the front edge so it sits above the tub rim.
      const doorX = halfDepth - tsInset * 0.5;
      ctx.save();
      ctx.lineWidth = (ctx.lineWidth || 1) * 2;
      ctx.beginPath();
      ctx.moveTo(doorX, -halfWidth);
      ctx.lineTo(doorX, halfWidth);
      ctx.stroke();
      ctx.restore();
      break;
    }

    case 'vanity':
      // Draw as rectangle with sink circle
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // Draw sink basin with double line
      const basinRadius = Math.min(width, depth) * 0.3;
      ctx.beginPath();
      ctx.arc(0, 0, basinRadius, 0, Math.PI * 2);
      ctx.stroke();
      // Inner basin line
      ctx.beginPath();
      ctx.arc(0, 0, basinRadius * 0.85, 0, Math.PI * 2);
      ctx.stroke();
      break;

    case 'shower': {
      // Check if this is a diagonal corner shower (wallCount >= 2 with diagonal door)
      // Straight door corner showers render as regular rectangles
      const isDiagonalCornerShower =
        fullObject &&
        fullObject.wallCount >= 2 &&
        fullObject.doorType === 'diagonal' &&
        fullObject.attachedWallAngles &&
        fullObject.attachedWallAngles.length === 2;

      if (isDiagonalCornerShower) {
        // Draw corner shower using wall angles - exactly matching 3D viewer logic
        const wallAngles = fullObject.attachedWallAngles;
        const normal1 = {
          x: Math.sin(wallAngles[0]),
          y: Math.cos(wallAngles[0]),
        };
        const normal2 = {
          x: Math.sin(wallAngles[1]),
          y: Math.cos(wallAngles[1]),
        };

        const doorWidthMeters = 0.6096;
        const d = doorWidthMeters / (2 * Math.sqrt(2));

        const backCorner = {
          x: (-depth / 2) * normal1.x + (-width / 2) * normal2.x,
          y: (-depth / 2) * normal1.y + (-width / 2) * normal2.y,
        };
        const corner1 = {
          x: (-depth / 2) * normal1.x + (width / 2) * normal2.x,
          y: (-depth / 2) * normal1.y + (width / 2) * normal2.y,
        };
        const corner2 = {
          x: (depth / 2) * normal1.x + (-width / 2) * normal2.x,
          y: (depth / 2) * normal1.y + (-width / 2) * normal2.y,
        };
        const cutPointA = {
          x: (depth / 2 - 2 * d) * normal1.x + (width / 2) * normal2.x,
          y: (depth / 2 - 2 * d) * normal1.y + (width / 2) * normal2.y,
        };
        const cutPointB = {
          x: (depth / 2) * normal1.x + (width / 2 - 2 * d) * normal2.x,
          y: (depth / 2) * normal1.y + (width / 2 - 2 * d) * normal2.y,
        };

        ctx.beginPath();
        ctx.moveTo(backCorner.x, backCorner.y);
        ctx.lineTo(corner1.x, corner1.y);
        ctx.lineTo(cutPointA.x, cutPointA.y);
        ctx.lineTo(cutPointB.x, cutPointB.y);
        ctx.lineTo(corner2.x, corner2.y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        const cornerInsetWA = Math.min(width, depth) * 0.2;
        ctx.beginPath();
        ctx.moveTo(-cornerInsetWA, -cornerInsetWA);
        ctx.lineTo(cornerInsetWA, cornerInsetWA);
        ctx.moveTo(-cornerInsetWA, cornerInsetWA);
        ctx.lineTo(cornerInsetWA, -cornerInsetWA);
        ctx.stroke();

        const cornerDrainSizeWA = Math.min(width, depth) * 0.1;
        ctx.strokeRect(
          -cornerDrainSizeWA / 2,
          -cornerDrainSizeWA / 2,
          cornerDrainSizeWA,
          cornerDrainSizeWA
        );

        const csShowerHeadSize = Math.min(width, depth) * 0.12;
        const showerHeadPos = {
          x: backCorner.x * 0.6,
          y: backCorner.y * 0.6,
        };
        drawShowerHeadRings(
          ctx,
          showerHeadPos.x,
          showerHeadPos.y,
          csShowerHeadSize
        );
        break;
      }

      // Regular shower: Draw as square/rectangle with a drain in center
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);

      // Draw shower pan lines (X pattern to indicate drain)
      const inset = Math.min(width, depth) * 0.15;
      ctx.beginPath();
      // Diagonal lines forming X
      ctx.moveTo(-halfDepth + inset, -halfWidth + inset);
      ctx.lineTo(halfDepth - inset, halfWidth - inset);
      ctx.moveTo(-halfDepth + inset, halfWidth - inset);
      ctx.lineTo(halfDepth - inset, -halfWidth + inset);
      ctx.stroke();

      // Draw center drain as small square
      const drainSize = Math.min(width, depth) * 0.1;
      ctx.strokeRect(-drainSize / 2, -drainSize / 2, drainSize, drainSize);

      // Draw showerhead on back wall (viewed from above)
      const showerHeadSize = Math.min(width, depth) * 0.15;
      const showerHeadX = -halfDepth + showerHeadSize; // Position near back wall

      // Showerhead arm (pipe from wall to showerhead)
      const armWidth = showerHeadSize * 0.2;
      ctx.fillRect(-halfDepth, -armWidth / 2, showerHeadSize, armWidth);
      ctx.strokeRect(-halfDepth, -armWidth / 2, showerHeadSize, armWidth);

      // Showerhead - just the flat disc part viewed from above
      const showerHeadWidth = showerHeadSize * 1.2;
      const showerHeadDepth = showerHeadSize * 0.3;
      ctx.fillRect(
        showerHeadX - showerHeadDepth / 2,
        -showerHeadWidth / 2,
        showerHeadDepth,
        showerHeadWidth
      );
      ctx.strokeRect(
        showerHeadX - showerHeadDepth / 2,
        -showerHeadWidth / 2,
        showerHeadDepth,
        showerHeadWidth
      );
      break;
    }

    case 'shower-door':
      // Draw as rectangle with door lines
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // Draw sliding door lines
      ctx.beginPath();
      ctx.moveTo(-halfDepth * 0.5, -halfWidth);
      ctx.lineTo(-halfDepth * 0.5, halfWidth);
      ctx.moveTo(halfDepth * 0.5, -halfWidth);
      ctx.lineTo(halfDepth * 0.5, halfWidth);
      ctx.stroke();
      break;

    case 'wood-stove': {
      // A freestanding heater: cast-iron body with a glass door on the FRONT
      // and a flue circle at the back. Canvas X = depth axis, Y = width axis,
      // and +halfDepth is the front (into the room), matching the fireplace
      // case above.
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      // glass door across the front face
      const doorWidth = width * 0.66;
      const doorDepth = Math.min(depth * 0.12, 0.05);
      ctx.save();
      ctx.fillStyle = '#141414';
      ctx.fillRect(halfDepth - doorDepth, -doorWidth / 2, doorDepth, doorWidth);
      ctx.restore();
      ctx.strokeRect(
        halfDepth - doorDepth,
        -doorWidth / 2,
        doorDepth,
        doorWidth
      );
      // flue at the back
      ctx.beginPath();
      ctx.arc(
        -halfDepth + depth * 0.25,
        0,
        Math.min(width, depth) * 0.11,
        0,
        Math.PI * 2
      );
      ctx.stroke();
      break;
    }

    case 'fireplace':
      // Draw outer rectangle
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);

      // Draw trapezoid opening inside the fireplace
      const openingDepth = depth * 0.3; // How far back the opening goes
      const openingWidthFront = width * 0.6; // Front width of opening (wider)
      const openingWidthBack = width * 0.4; // Back width of opening (narrower)

      ctx.beginPath();
      // Start from front of fireplace (wider part)
      // Front left of trapezoid
      ctx.moveTo(halfDepth, -openingWidthFront / 2);
      // Front right of trapezoid
      ctx.lineTo(halfDepth, openingWidthFront / 2);
      // Back right of trapezoid (narrower)
      ctx.lineTo(halfDepth - openingDepth, openingWidthBack / 2);
      // Back left of trapezoid (narrower)
      ctx.lineTo(halfDepth - openingDepth, -openingWidthBack / 2);
      ctx.closePath();

      // Fill with same color as fireplace body
      ctx.fill();
      // Just stroke the outline
      ctx.stroke();
      break;

    case 'stairs': {
      // A WINDER turns while climbing, so its treads fan from the inside corner
      // instead of running parallel across the flight. The pipeline stamps the
      // turn and the bearing it lets out at (`stairTurn`), because only it can
      // see the flights either side — see
      // shared-lib/split-level/reconstructWinders.js.
      const turn = fullObject?.stairTurn;
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);

      if (turn) {
        // Same construction as the pipeline and the 3D renderer: the fan ends
        // on the last tread's nosing bearing and works backwards, with the
        // pivot recovered by centring the fan's own bounds in the box.
        //
        // FRAME: the caller has already applied `ctx.rotate(objectAngle)` with
        // `objectAngle = atan2(localZ.z, localZ.x)`, so inside here canvas x is
        // the object's DEPTH axis and canvas y is the NEGATIVE of its width
        // axis. Working that through, a world bearing A lands at
        //   canvas x =  sin(A + ry)
        //   canvas y = -cos(A + ry)
        // Getting either the transposition or that minus sign wrong leaves the
        // fan mirrored while the 3D view looks right, so it is derived here
        // rather than matched by eye.
        const ry = fullObject?.rotation?.y || 0;
        const sweep = ((turn.sweepDeg ?? 90) * Math.PI) / 180;
        const aEnd = ((turn.endNosingDeg ?? 0) * Math.PI) / 180;
        const aStart = aEnd - sweep;
        const cx = (a) => Math.sin(a + ry);
        const cy = (a) => -Math.cos(a + ry);
        let fMinX = 0;
        let fMaxX = 0;
        let fMinY = 0;
        let fMaxY = 0;
        for (let i = 0; i <= 64; i++) {
          const a = aStart + ((aEnd - aStart) * i) / 64;
          fMinX = Math.min(fMinX, cx(a));
          fMaxX = Math.max(fMaxX, cx(a));
          fMinY = Math.min(fMinY, cy(a));
          fMaxY = Math.max(fMaxY, cy(a));
        }
        const reach = Math.min(
          depth / (fMaxX - fMinX || 1),
          width / (fMaxY - fMinY || 1)
        );
        const pivotX = -((fMinX + fMaxX) / 2) * reach;
        const pivotY = -((fMinY + fMaxY) / 2) * reach;
        // Treads fill the corner, so each nosing runs out to the box edge
        // rather than to a fixed radius — square print, not a disc, matching
        // the 3D renderer.
        const edgeReach = (a) => {
          const dx = cx(a);
          const dy = cy(a);
          let t = Infinity;
          if (Math.abs(dx) > 1e-9) {
            t = Math.min(
              t,
              Math.max((halfDepth - pivotX) / dx, (-halfDepth - pivotX) / dx)
            );
          }
          if (Math.abs(dy) > 1e-9) {
            t = Math.min(
              t,
              Math.max((halfWidth - pivotY) / dy, (-halfWidth - pivotY) / dy)
            );
          }
          return Number.isFinite(t) ? t : 0;
        };
        const treads = Math.max(2, Math.round(turn.steps ?? 4));
        for (let i = 0; i <= treads; i++) {
          const a = aStart + ((aEnd - aStart) * i) / treads;
          const r = edgeReach(a);
          ctx.beginPath();
          ctx.moveTo(pivotX, pivotY);
          ctx.lineTo(pivotX + r * cx(a), pivotY + r * cy(a));
          ctx.stroke();
        }
        break;
      }

      // Step lines
      const numSteps = Math.max(6, Math.floor(depth / 0.3));
      const stepSize = depth / numSteps;
      for (let i = 1; i < numSteps; i++) {
        const stepX = -halfDepth + i * stepSize;
        ctx.beginPath();
        ctx.moveTo(stepX, -halfWidth);
        ctx.lineTo(stepX, halfWidth);
        ctx.stroke();
      }

      // Draw "UP" text and arrow at the front edge (where orientation arrow points)
      ctx.save();

      // Set text properties - use world coordinates with slightly bigger size
      const textSize = 0.12; // Slightly bigger size in world units (12cm)
      ctx.font = `${textSize}px Arial`; // Font size in world units
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#000000'; // Black text
      ctx.strokeStyle = '#000000'; // Black for arrow

      // Position at the front edge of stairs (positive X direction - where the normal/orientation points)
      // This is the edge at X = halfDepth
      const textX = halfDepth - textSize; // Just inside the front edge
      const textY = 0; // Center vertically

      // Save position and rotate for text only
      ctx.save();
      ctx.translate(textX, textY);
      ctx.rotate(-Math.PI / 2); // Rotate 90 degrees counter-clockwise

      // Draw "UP" text in black (now rotated)
      ctx.fillText('UP', 0, 0);

      ctx.restore(); // Restore rotation but keep other styles

      // Draw arrow pointing backward (toward the back of the stairs)
      const arrowLength = stepSize * 1.5; // Arrow length
      const arrowStartX = textX - textSize * 2; // Just to the left of text
      const arrowStartY = 0; // Keep centered vertically
      const arrowEndX = arrowStartX - arrowLength; // Point toward back of stairs
      const arrowEndY = 0; // Keep centered
      const arrowHeadSize = 0.05; // Small arrowhead in world units

      ctx.lineWidth = 0.01; // Very thin line in world units
      ctx.beginPath();
      // Arrow line
      ctx.moveTo(arrowStartX, arrowStartY);
      ctx.lineTo(arrowEndX, arrowEndY);

      // Arrow head (pointing left/toward back of stairs)
      ctx.moveTo(arrowEndX, arrowEndY);
      ctx.lineTo(arrowEndX + arrowHeadSize, arrowEndY - arrowHeadSize * 0.7);
      ctx.moveTo(arrowEndX, arrowEndY);
      ctx.lineTo(arrowEndX + arrowHeadSize, arrowEndY + arrowHeadSize * 0.7);

      ctx.stroke();
      ctx.restore();
      break;
    }

    case 'wall-light':
    case 'walllight':
    case 'overhead-wall-light': // Support legacy type
      // Draw wall light as a stem with a circle and inner yellow light
      const circleRadius = Math.min(width, depth) * 0.35;
      const stemLength = Math.min(width, depth) * 0.3;
      const stemWidth = Math.min(width, depth) * 0.08;

      // Draw stem (mounting bracket) from wall at the BACK (against the wall)
      // Wall lights mount at the back and project forward into the room
      ctx.fillRect(-halfDepth, -stemWidth / 2, stemLength, stemWidth);
      ctx.strokeRect(-halfDepth, -stemWidth / 2, stemLength, stemWidth);

      // Draw outer circle (light fixture) projecting forward from the wall
      ctx.beginPath();
      ctx.arc(
        -halfDepth + stemLength + circleRadius,
        0,
        circleRadius,
        0,
        Math.PI * 2
      );
      ctx.fill();
      ctx.stroke();

      // Draw inner yellow circle (representing the light)
      ctx.beginPath();
      ctx.arc(
        -halfDepth + stemLength + circleRadius,
        0,
        circleRadius * 0.6, // Inner circle is 60% of outer
        0,
        Math.PI * 2
      );
      ctx.fillStyle = '#FFD700'; // Gold/yellow color for the light
      ctx.fill();

      // Optional: Add a subtle glow effect
      ctx.save();
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = ctx.lineWidth * 0.5;
      ctx.stroke();
      ctx.restore();
      break;

    case 'mirror':
      // Draw mirror in top-down view - we only see the thin edge
      // The mirror mounts flat against the wall at -halfDepth (back)
      // Projects forward by depth (0.05m thin)
      // Extends width (0.61m) along the wall

      // Main mirror body - just a thin rectangle from top view
      ctx.fillRect(
        -halfDepth, // Start at back (against wall)
        -halfWidth, // Center along wall
        depth, // Project forward from wall (0.05m thin edge)
        width // Extent along wall (0.61m wide)
      );

      // Draw frame border
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);

      // In top-down view, we can't see the reflective face
      // Just add a subtle center line to indicate it's a mirror (not just a shelf)
      ctx.save();
      ctx.strokeStyle = 'rgba(200, 200, 200, 0.5)';
      ctx.lineWidth = 0.5 / scale;
      ctx.beginPath();
      // Draw a line through the middle to show it's a special object
      ctx.moveTo(0, -halfWidth + width * 0.1);
      ctx.lineTo(0, halfWidth - width * 0.1);
      ctx.stroke();
      ctx.restore();
      break;

    case 'countertop':
      // a slab with an outline of its own — cut at 45° over an angled end —
      // draws it; else a simple rectangle (same as default case)
      if (
        drawWorldOutline(
          ctx,
          fullObject?.polygonCorners,
          fullObject?.center,
          objectAngle
        )
      )
        break;
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      break;

    case 'corner-fill-cabinet':
    case 'corner-fill-countertop': {
      // Polygon-shaped countertop bridging the void at a non-right-
      // angle corner. The outline is stored in world coordinates as
      // `fullObject.polygonCorners`. drawObject has already applied
      // `ctx.translate(center.x, center.z)` and `ctx.rotate(objectAngle)`
      // before getting here, so we convert each vertex from world (x, z)
      // to the post-rotation local frame:
      //   localX = (Δx) * cos(θ) + (Δz) * sin(θ)
      //   localY = -(Δx) * sin(θ) + (Δz) * cos(θ)
      // where Δx = vertex.x - center.x, Δz = vertex.z - center.z, θ = objectAngle.
      // Falls through to the default rectangle if the polygon outline
      // isn't available.
      if (
        !drawWorldOutline(
          ctx,
          fullObject?.polygonCorners,
          fullObject?.center,
          objectAngle
        )
      ) {
        ctx.fillRect(-halfDepth, -halfWidth, depth, width);
        ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      }
      break;
    }

    case 'ceiling-light':
    case 'ceilinglight':
      // Draw ceiling light as a circle with decorative pattern and yellow center
      const lightRadius = Math.min(width, depth) * 0.35;

      // Draw outer circle (light fixture)
      ctx.beginPath();
      ctx.arc(0, 0, lightRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Draw cross pattern (decorative mounting)
      ctx.beginPath();
      ctx.moveTo(-lightRadius, 0);
      ctx.lineTo(lightRadius, 0);
      ctx.moveTo(0, -lightRadius);
      ctx.lineTo(0, lightRadius);
      ctx.stroke();

      // Draw corner decorations
      ctx.beginPath();
      const cornerOffset = lightRadius * 0.7;
      ctx.moveTo(-cornerOffset, -cornerOffset);
      ctx.lineTo(-cornerOffset * 0.5, -cornerOffset * 0.5);
      ctx.moveTo(cornerOffset, -cornerOffset);
      ctx.lineTo(cornerOffset * 0.5, -cornerOffset * 0.5);
      ctx.moveTo(-cornerOffset, cornerOffset);
      ctx.lineTo(-cornerOffset * 0.5, cornerOffset * 0.5);
      ctx.moveTo(cornerOffset, cornerOffset);
      ctx.lineTo(cornerOffset * 0.5, cornerOffset * 0.5);
      ctx.stroke();

      // Draw middle circle outline
      ctx.beginPath();
      ctx.arc(0, 0, lightRadius * 0.5, 0, Math.PI * 2);
      ctx.stroke();

      // Draw inner yellow circle (representing the light bulb)
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, lightRadius * 0.25, 0, Math.PI * 2);
      ctx.fillStyle = '#FFD700'; // Gold/yellow color for the light
      ctx.fill();

      // Optional: Add a subtle glow effect around the yellow circle
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = ctx.lineWidth * 0.5;
      ctx.stroke();
      ctx.restore();
      break;

    default: {
      // Dorm objects — draw shape + upright badge text
      const ot = objectType.toLowerCase();
      if (ot.startsWith('dorm-')) {
        const badge = getDormBadge(ot);

        if (ot.startsWith('dorm-bed') || ot.startsWith('dorm-bunk')) {
          // Bed: rectangle + inner mattress inset + pillow at head
          ctx.fillRect(-halfDepth, -halfWidth, depth, width);
          ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
          const inset = Math.min(depth, width) * 0.06;
          ctx.strokeRect(
            -halfDepth + inset,
            -halfWidth + inset,
            depth - inset * 2,
            width - inset * 2
          );
          // Pillow at head end (-depth side)
          const pillowD = depth * 0.12;
          const pillowW = width * 0.6;
          ctx.fillRect(
            -halfDepth + inset * 1.5,
            -pillowW / 2,
            pillowD,
            pillowW
          );
          ctx.strokeRect(
            -halfDepth + inset * 1.5,
            -pillowW / 2,
            pillowD,
            pillowW
          );
        } else if (ot === 'dorm-dresser' || ot === 'dorm-stackable-drawers') {
          // Dresser: rectangle + horizontal drawer lines
          ctx.fillRect(-halfDepth, -halfWidth, depth, width);
          ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
          const drawerCount = ot === 'dorm-stackable-drawers' ? 3 : 2;
          for (let i = 1; i < drawerCount; i++) {
            const lineY = -halfWidth + (width * i) / drawerCount;
            ctx.beginPath();
            ctx.moveTo(-halfDepth, lineY);
            ctx.lineTo(halfDepth, lineY);
            ctx.stroke();
          }
        } else if (ot === 'dorm-rug' || ot === 'dorm-rug-circular') {
          // Rug: dashed outline, translucent fill
          ctx.save();
          ctx.globalAlpha = 0.3;
          if (ot === 'dorm-rug-circular') {
            ctx.beginPath();
            ctx.ellipse(0, 0, halfDepth, halfWidth, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 0.6;
            ctx.setLineDash([0.05, 0.05]);
            ctx.stroke();
          } else {
            ctx.fillRect(-halfDepth, -halfWidth, depth, width);
            ctx.globalAlpha = 0.6;
            ctx.setLineDash([0.05, 0.05]);
            ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
          }
          ctx.restore();
        } else if (isDormSmallDecor(ot)) {
          // Small surface decor (knickknacks, desk plants): an unfilled dot.
          // These sit ON desks/dressers in plan view — an opaque rectangle
          // with a drawer-pull line stacked on the desk rectangle reads as a
          // mystery cabinet; a light open circle reads as "small object here".
          ctx.save();
          ctx.globalAlpha = 0.7;
          ctx.beginPath();
          ctx.ellipse(0, 0, halfDepth, halfWidth, 0, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        } else {
          // All other dorm items: rectangle + handle line
          ctx.fillRect(-halfDepth, -halfWidth, depth, width);
          ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
          ctx.beginPath();
          ctx.moveTo(halfDepth - 0.03, -halfWidth * 0.5);
          ctx.lineTo(halfDepth - 0.03, halfWidth * 0.5);
          ctx.stroke();
        }

        // Badge text — counter-rotate so it stays upright
        if (badge) {
          ctx.save();
          ctx.rotate(-objectAngle);
          ctx.fillStyle = '#000000';
          const fs =
            Math.max(8, Math.min(12, Math.min(width, depth) * scale * 0.4)) /
            scale;
          ctx.font = `300 ${fs}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(badge, 0, 0);
          ctx.restore();
        }

        break;
      }

      // Default: simple rectangle
      ctx.fillRect(-halfDepth, -halfWidth, depth, width);
      ctx.strokeRect(-halfDepth, -halfWidth, depth, width);
      break;
    }
  }
};

// Helper function to draw rounded rectangles
export const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  drawRoundedRectPath(ctx, x, y, width, height, radius);
  ctx.fill();
  ctx.stroke();
};

// Helper function to create rounded rectangle path without filling/stroking
export const drawRoundedRectPath = (ctx, x, y, width, height, radius) => {
  const r = radius * Math.min(width, height);
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};
